import React, { useState, useEffect, useCallback } from 'react';
import './AnimatedDemo.css';

const TICKERS = [
  { sym: 'S&P 500', val: 5842, chg: +0.74 },
  { sym: 'NASDAQ', val: 18293, chg: +1.12 },
  { sym: 'DOW', val: 43127, chg: -0.23 },
  { sym: 'BTC', val: 67285, chg: +2.4 },
  { sym: 'VIX', val: 14.8, chg: -3.2 },
];

const HEATMAP = [
  { sym: 'AAPL', chg: 0.8 }, { sym: 'MSFT', chg: 1.2 }, { sym: 'NVDA', chg: 2.5 },
  { sym: 'AMZN', chg: -0.4 }, { sym: 'META', chg: 1.8 },
  { sym: 'GOOGL', chg: 0.3 }, { sym: 'TSLA', chg: -1.8 }, { sym: 'JPM', chg: 0.6 },
  { sym: 'JNJ', chg: -0.2 }, { sym: 'V', chg: 0.9 },
];

const MA_DATA = [
  { label: 'SMA 10', val: 584.2, above: true },
  { label: 'EMA 21', val: 578.6, above: true },
  { label: 'SMA 50', val: 562.1, above: true },
  { label: 'SMA 200', val: 534.8, above: true },
];

export default function MarketTrendsDemo({ autoPlay = true, loop = true }) {
  const [phase, setPhase] = useState(0);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [heatVisible, setHeatVisible] = useState(0);
  const [maVisible, setMaVisible] = useState(0);

  const heatColor = (chg) => {
    if (chg >= 2) return '#10B981';
    if (chg >= 0.5) return '#34D399';
    if (chg >= 0) return '#6EE7B7';
    if (chg >= -1) return '#FCA5A5';
    return '#EF4444';
  };

  const runDemo = useCallback(() => {
    setPhase(0); setTickerIdx(0); setHeatVisible(0); setMaVisible(0);

    // Phase 1: Show tickers one by one
    setTimeout(() => {
      setPhase(1);
      let idx = 0;
      const tickInterval = setInterval(() => {
        idx++;
        setTickerIdx(idx);
        if (idx >= TICKERS.length) clearInterval(tickInterval);
      }, 300);
    }, 500);

    // Phase 2: Show heatmap
    setTimeout(() => {
      setPhase(2);
      let h = 0;
      const heatInterval = setInterval(() => {
        h++;
        setHeatVisible(h);
        if (h >= HEATMAP.length) clearInterval(heatInterval);
      }, 150);
    }, 3000);

    // Phase 3: Show MAs
    setTimeout(() => {
      setPhase(3);
      let m = 0;
      const maInterval = setInterval(() => {
        m++;
        setMaVisible(m);
        if (m >= MA_DATA.length) clearInterval(maInterval);
      }, 400);
    }, 5500);
  }, []);

  useEffect(() => {
    if (!autoPlay) return;
    runDemo();
    if (!loop) return;
    const loopId = setInterval(runDemo, 11000);
    return () => clearInterval(loopId);
  }, [autoPlay, loop, runDemo]);

  return (
    <div className="demo-container">
      <div className="demo-titlebar">
        <span className="demo-dot red" /><span className="demo-dot yellow" /><span className="demo-dot green" />
        <span className="demo-titlebar-text">alphagrade.io/trends</span>
      </div>
      <div className="demo-body">
        {/* Market tickers */}
        <div className="demo-ticker-row">
          {TICKERS.slice(0, tickerIdx).map((t) => (
            <div key={t.sym} className="demo-ticker-card" style={{ animation: 'slideInUp 0.3s ease both' }}>
              <span className="demo-ticker-sym">{t.sym}</span>
              <span className="demo-ticker-val">{typeof t.val === 'number' && t.val > 1000 ? t.val.toLocaleString() : t.val}</span>
              <span className={`demo-ticker-chg ${t.chg >= 0 ? 'pos' : 'neg'}`}>
                {t.chg >= 0 ? '+' : ''}{t.chg}%
              </span>
            </div>
          ))}
        </div>

        {/* Mini heatmap */}
        {phase >= 2 && (
          <>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase' }}>Market Heatmap</div>
            <div className="demo-heatmap">
              {HEATMAP.slice(0, heatVisible).map((h) => (
                <div key={h.sym} className="demo-heat-cell" style={{
                  background: heatColor(h.chg),
                  animation: 'fadeIn 0.2s ease both',
                }}>
                  <div>{h.sym}</div>
                  <div>{h.chg >= 0 ? '+' : ''}{h.chg}%</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Moving Averages */}
        {phase >= 3 && (
          <>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.4rem', fontWeight: 600, textTransform: 'uppercase', marginTop: '0.75rem' }}>SPY Moving Averages</div>
            {MA_DATA.slice(0, maVisible).map((ma) => (
              <div key={ma.label} className="demo-score-row" style={{ animation: 'slideInRight 0.3s ease both' }}>
                <span className="demo-score-label">{ma.label}</span>
                <span className="demo-score-val" style={{ color: ma.above ? '#10B981' : '#EF4444', minWidth: 45 }}>{ma.val}</span>
                <span style={{ fontSize: '0.65rem', color: ma.above ? '#10B981' : '#EF4444' }}>{ma.above ? '▲ Above' : '▼ Below'}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
